import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import type * as React from 'react';

import { cn } from '~/utils/cn.ts';

import { type OverlayTransitions, overlayMotionVariants } from '../utils/animations';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;

function DialogPortal({
	children,
	className,
	...props
}: DialogPrimitive.DialogPortalProps & {
	className?: string;
}) {
	return (
		<DialogPrimitive.Portal {...props}>
			<div
				className={cn('fixed inset-0 z-[var(--z-index-dialog)] flex items-start justify-center', className)}
				data-slot="dialog-portal"
			>
				{children}
			</div>
		</DialogPrimitive.Portal>
	);
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			className={cn(
				'fixed inset-0 z-[var(--z-index-dialog)] bg-background/80 backdrop-blur-xs transition-all duration-100',
				className,
			)}
			data-slot="dialog-overlay"
			{...props}
		/>
	);
}

const AnimatedOverlay = motion('div');

interface CustomAnimatedDialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Content> {
	isCloseable?: boolean;
	transition?: OverlayTransitions;
	classNames: {
		content?: string;
		overlay?: string;
		portal?: string;
	};
	open: boolean | undefined;
}

function DialogContent({
	classNames,
	children,
	isCloseable = true,
	transition = 'fromLeft',
	open,
	...props
}: CustomAnimatedDialogContentProps) {
	const overlayTransitions = overlayMotionVariants[transition];

	return (
		<AnimatePresence mode="wait">
			{open && (
				<DialogPortal className={cn(classNames.portal, open ? 'visible' : 'hidden')} forceMount>
					<DialogOverlay className={classNames.overlay} />
					<DialogPrimitive.Content
						asChild
						// Lyra style: sharp corners
						className={cn(
							'fixed z-[calc(var(--z-index-dialog)+1)] grid w-full gap-4 rounded-none border bg-background p-6 shadow-lg sm:max-w-lg',
							classNames.content,
						)}
						{...props}
					>
						<AnimatedOverlay
							animate={overlayTransitions.animate}
							exit={overlayTransitions.exit}
							initial={overlayTransitions.initial}
							variants={overlayTransitions}
						>
							{children}
							{isCloseable && (
								<DialogPrimitive.Close
									// Lyra style: sharp corners
									className="absolute top-4 right-4 rounded-none opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
									data-slot="dialog-close"
								>
									<X className="size-4" />
									<span className="sr-only">Close</span>
								</DialogPrimitive.Close>
							)}
						</AnimatedOverlay>
					</DialogPrimitive.Content>
				</DialogPortal>
			)}
		</AnimatePresence>
	);
}

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn('flex flex-col gap-y-1.5 text-center sm:text-left', className)}
			data-slot="dialog-header"
			{...props}
		/>
	);
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-x-2', className)}
			data-slot="dialog-footer"
			{...props}
		/>
	);
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			// Lyra style: text-base for titles
			className={cn('font-semibold text-base leading-none tracking-tight', className)}
			data-slot="dialog-title"
			{...props}
		/>
	);
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			// Lyra style: text-xs for descriptions
			className={cn('text-muted-foreground text-xs', className)}
			data-slot="dialog-description"
			{...props}
		/>
	);
}

export { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger };
